import React, { useEffect, useState } from "react";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import TextInput from "../../components/UI/TextInput";
import Table from "../../components/UI/Table";
import { Button } from "../../components/UI/Button";
import { LucidePlus, Edit, Trash2 } from "lucide-react";
import Modal from "../../components/UI/Modal";
import Switch from "../../components/UI/Switch";
import TextArea from "../../components/UI/TextArea";
import { useAuthStore } from "../../stores/auth-store";
import { contentUseCase } from "../../../di/container";
import type { Font } from "../../../domain/types/content";

const FONT_PRESETS = [
  {
    fontName: "Noto Sans KR",
    fontFamily: "Noto Sans KR",
    fontType: "Sans-serif",
    importText: "@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@100..900&display=swap');",
  },
  {
    fontName: "Pretendard",
    fontFamily: "Pretendard",
    fontType: "Gothic",
    importText: "@import url(\"https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css\");",
  },
  {
    fontName: "나눔고딕",
    fontFamily: "NanumGothic",
    fontType: "Gothic",
    importText: "@import url('https://fonts.googleapis.com/css2?family=Nanum+Gothic:wght@400;700;800&display=swap');",
  },
  {
    fontName: "나눔명조",
    fontFamily: "NanumMyeongjo",
    fontType: "Serif",
    importText: "@import url('https://fonts.googleapis.com/css2?family=Nanum+Myeongjo:wght@400;700;800&display=swap');",
  },
  {
    fontName: "IBM Plex Sans KR",
    fontFamily: "IBM Plex Sans KR",
    fontType: "Sans-serif",
    importText: "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+KR:wght@100;200;300;400;500;600;700&display=swap');",
  },
  {
    fontName: "Gmarket Sans",
    fontFamily: "GmarketSans",
    fontType: "Sans-serif",
    importText: "@font-face {\n    font-family: 'GmarketSans';\n    src: url('https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_2001@1.1/GmarketSansMedium.woff') format('woff');\n    font-weight: normal;\n    font-style: normal;\n}",
  },
  {
    fontName: "코트라 희망체",
    fontFamily: "KOTRAHOPE",
    fontType: "Sans-serif",
    importText: "@font-face {\n    font-family: 'KOTRAHOPE';\n    src: url('https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_2110@1.0/KOTRAHOPE.woff2') format('woff2');\n    font-weight: normal;\n    font-style: normal;\n}",
  },
  {
    fontName: "코트라 볼드체",
    fontFamily: "KOTRA_BOLD-Bold",
    fontType: "Sans-serif",
    importText: "@font-face {\n    font-family: 'KOTRA_BOLD-Bold';\n    src: url('https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_20-10-21@1.1/KOTRA_BOLD-Bold.woff') format('woff');\n    font-weight: normal;\n    font-style: normal;\n}",
  },
];

const FontManagement: React.FC = () => {
  const { user } = useAuthStore();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [loadedData, setLoadedData] = useState<Font[]>([]);

  // 추가 모달 상태
  const [isNewFontModalVisible, setIsNewFontModalVisible] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("");
  const [newFontName, setNewFontName] = useState("");
  const [newFontFamily, setNewFontFamily] = useState("");
  const [newFontType, setNewFontType] = useState("");
  const [newFontImportText, setNewFontImportText] = useState("");
  const [newFontIsBeingUsed, setNewFontIsBeingUsed] = useState(false);

  // 수정 모달 상태
  const [editFontId, setEditFontId] = useState<number | null>(null);
  const [isEditFontModalVisible, setIsEditFontModalVisible] = useState(false);
  const [editSelectedPreset, setEditSelectedPreset] = useState("");
  const [editFontName, setEditFontName] = useState("");
  const [editFontFamily, setEditFontFamily] = useState("");
  const [editFontType, setEditFontType] = useState("");
  const [editFontImportText, setEditFontImportText] = useState("");
  const [editFontIsBeingUsed, setEditFontIsBeingUsed] = useState(false);

  const loadData = async () => {
    if (!user?.companyId) return;
    try {
      const data = await contentUseCase.getFonts(user.companyId);
      setLoadedData(data);
    } catch (error) {
      alert("폰트 목록을 불러오는데 실패했습니다.");
    }
  };

  useEffect(() => {
    if (user?.companyId) {
      loadData();
    }
  }, [user?.companyId]);

  const resetNewModal = () => {
    setSelectedPreset("");
    setNewFontName("");
    setNewFontFamily("");
    setNewFontType("");
    setNewFontImportText("");
    setNewFontIsBeingUsed(false);
  };

  const handleCreate = async () => {
    if (!user?.companyId) return;
    if (!newFontName || !newFontFamily || !newFontType || !newFontImportText) {
      alert("필수 항목이 비어있습니다!");
      return;
    }

    try {
      await contentUseCase.createFont(user.companyId, {
        fontName: newFontName,
        fontFamily: newFontFamily,
        fontType: newFontType,
        importText: newFontImportText,
        isBeingUsed: newFontIsBeingUsed,
      });
      await loadData();
      setIsNewFontModalVisible(false);
      resetNewModal();
    } catch (error) {
      alert("폰트 생성에 실패했습니다.");
    }
  };

  const handleUpdate = async () => {
    if (!user?.companyId || editFontId === null) return;
    if (!editFontName || !editFontFamily || !editFontType || !editFontImportText) {
      alert("필수 항목이 비어있습니다!");
      return;
    }

    const existing = loadedData.find((f) => f.id === editFontId);
    if (!existing) return;

    try {
      await contentUseCase.updateFont(user.companyId, editFontId, {
        fontName: editFontName,
        fontFamily: editFontFamily,
        fontType: editFontType,
        importText: editFontImportText,
        isBeingUsed: existing.isBeingUsed,
      });
      await loadData();
      setIsEditFontModalVisible(false);
    } catch (error) {
      alert("폰트 수정에 실패했습니다.");
    }
  };

  const handleToggleUsed = async (font: Font) => {
    if (!user?.companyId) return;
    try {
      await contentUseCase.updateFont(user.companyId, font.id, {
        fontName: font.fontName,
        fontFamily: font.fontFamily,
        fontType: font.fontType,
        importText: font.importText,
        isBeingUsed: !font.isBeingUsed,
      });
      await loadData();
    } catch (error) {
      alert("사용여부 변경에 실패했습니다.");
    }
  };

  const handleDelete = async (fontId: number) => {
    if (!user?.companyId) return;
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      await contentUseCase.deleteFont(user.companyId, fontId);
      await loadData();
    } catch (error) {
      alert("폰트 삭제에 실패했습니다.");
    }
  };

  return (
    <>
      {isEditFontModalVisible && (
        <Modal setIsModalVisible={setIsEditFontModalVisible} title="폰트 수정">
          <>
            <div className="w-[calc(100vw-20vw)] max-w-[31.875rem] mb-4" />

            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                추천 폰트에서 선택
              </label>
              <select
                className="w-full border border-[#e0e0e0] rounded-[8px] p-2 bg-gray-50 text-sm"
                value={editSelectedPreset}
                onChange={(e) => {
                  const val = e.target.value;
                  setEditSelectedPreset(val);
                  if (val === "") return;
                  const preset = FONT_PRESETS[Number(val)];
                  if (!preset) return;
                  setEditFontName(preset.fontName);
                  setEditFontFamily(preset.fontFamily);
                  setEditFontType(preset.fontType);
                  setEditFontImportText(preset.importText);
                }}
              >
                <option value="">직접 입력</option>
                {FONT_PRESETS.map((preset, idx) => (
                  <option key={idx} value={idx}>
                    {preset.fontName} ({preset.fontType})
                  </option>
                ))}
              </select>
            </div>

            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                폰트 이름
              </label>
              <TextInput
                value={editFontName}
                onChange={(e) => setEditFontName(e.target.value)}
                placeholder="Interop"
                className="bg-gray-50"
              />
            </div>
            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                폰트 패밀리
              </label>
              <TextInput
                value={editFontFamily}
                onChange={(e) => setEditFontFamily(e.target.value)}
                placeholder="Interop"
                className="bg-gray-50"
              />
            </div>
            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                폰트 타입
              </label>
              <TextInput
                value={editFontType}
                onChange={(e) => setEditFontType(e.target.value)}
                placeholder="Gothic"
                className="bg-gray-50"
              />
            </div>
            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                폰트 임포트
              </label>
              <TextArea
                value={editFontImportText}
                onChange={(e) => setEditFontImportText(e.target.value)}
                placeholder={`@font-face {
    font-family: 'KCCChassam';
    src: url('https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_2302@1.0/KCCChassam.woff2') format('woff2');
    font-weight: normal;
    font-style: normal;
}`}
                className="bg-gray-50"
              />
            </div>
            <Button onClick={handleUpdate} className="w-full h-[45px]">
              제출
            </Button>
          </>
        </Modal>
      )}

      {isNewFontModalVisible && (
        <Modal setIsModalVisible={setIsNewFontModalVisible} title="폰트 추가">
          <>
            <div className="w-[calc(100vw-20vw)] max-w-[31.875rem] mb-4" />

            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                추천 폰트에서 선택
              </label>
              <select
                className="w-full border border-[#e0e0e0] rounded-[8px] p-2 bg-gray-50 text-sm"
                value={selectedPreset}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedPreset(val);
                  if (val === "") {
                    resetNewModal();
                    return;
                  }
                  const preset = FONT_PRESETS[Number(val)];
                  if (!preset) return;
                  setNewFontName(preset.fontName);
                  setNewFontFamily(preset.fontFamily);
                  setNewFontType(preset.fontType);
                  setNewFontImportText(preset.importText);
                }}
              >
                <option value="">직접 입력</option>
                {FONT_PRESETS.map((preset, idx) => (
                  <option key={idx} value={idx}>
                    {preset.fontName} ({preset.fontType})
                  </option>
                ))}
              </select>
            </div>

            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                폰트 이름
              </label>
              <TextInput
                value={newFontName}
                onChange={(e) => setNewFontName(e.target.value)}
                placeholder="Interop"
                className="bg-gray-50"
              />
            </div>
            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                폰트 패밀리
              </label>
              <TextInput
                value={newFontFamily}
                onChange={(e) => setNewFontFamily(e.target.value)}
                placeholder="Interop"
                className="bg-gray-50"
              />
            </div>
            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                폰트 타입
              </label>
              <TextInput
                value={newFontType}
                onChange={(e) => setNewFontType(e.target.value)}
                placeholder="Gothic"
                className="bg-gray-50"
              />
            </div>
            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                폰트 임포트
              </label>
              <TextArea
                value={newFontImportText}
                onChange={(e) => setNewFontImportText(e.target.value)}
                placeholder={`@font-face {
    font-family: 'KCCChassam';
    src: url('https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_2302@1.0/KCCChassam.woff2') format('woff2');
    font-weight: normal;
    font-style: normal;
}`}
                className="bg-gray-50"
              />
            </div>
            <Button onClick={handleCreate} className="w-full h-[45px]">
              제출
            </Button>
          </>
        </Modal>
      )}

      <CompanyAdminLayout>
        <div className="p-5">
          <div className="mb-[28px] / flex flex-row justify-between gap-[0.5rem]">
            <h2 className="text-[1.375rem] / font-semibold">폰트 관리</h2>
            <Button onClick={() => {
              resetNewModal();
              setIsNewFontModalVisible(true);
            }}>
              <LucidePlus className="scale-[0.9]" />
              추가하기
            </Button>
          </div>

          <div
            className="mb-[1.25rem] / p-[1.25rem]
          / border border-[#d9d9d9] / rounded-[0.625rem] / bg-white
          / flex flex-col md:flex-row gap-[0.75rem] / justify-stretch"
          >
            <TextInput
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="w-full"
              placeholder="폰트 이름, 폰트 패밀리, 폰트 타입으로 검색"
            />
          </div>
          <div className="text-[12px]">
            <Table
              tabledata={{
                head: [
                  <>폰트 이름</>,
                  <>폰트 패밀리</>,
                  <>폰트 타입</>,
                  <>등록일</>,
                  <>사용 여부</>,
                  <></>,
                ],
                body: loadedData
                  .filter((item) => {
                    if (searchKeyword === "") return true;
                    return (
                      item.fontName
                        .toLowerCase()
                        .includes(searchKeyword.toLowerCase()) ||
                      item.fontFamily
                        .toLowerCase()
                        .includes(searchKeyword.toLowerCase()) ||
                      item.fontType
                        .toLowerCase()
                        .includes(searchKeyword.toLowerCase())
                    );
                  })
                  .map((item) => [
                    <>{item.fontName}</>,
                    <>{item.fontFamily}</>,
                    <>{item.fontType}</>,
                    <>{new Date(item.createdAt).toISOString().slice(0, 10)}</>,
                    <div onClick={() => handleToggleUsed(item)} className="cursor-pointer">
                      <Switch isEnabled={item.isBeingUsed} />
                    </div>,
                    <div className="flex flex-row gap-[6px]">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditFontId(item.id);
                          setEditSelectedPreset("");
                          setEditFontName(item.fontName);
                          setEditFontFamily(item.fontFamily);
                          setEditFontType(item.fontType);
                          setEditFontIsBeingUsed(item.isBeingUsed);
                          setEditFontImportText(item.importText);
                          setIsEditFontModalVisible(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                        수정
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                        삭제
                      </Button>
                    </div>,
                  ]),
              }}
            />
          </div>
        </div>
      </CompanyAdminLayout>
    </>
  );
};

export default FontManagement;